import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { useApi, useApiProblem, type MessageSendResolutionRequest } from '@/shared/api';

export function useSendResolution(messageId: string) {
  const api = useApi();
  const cache = useQueryClient();
  const inFlight = useRef(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const query = useQuery({
    queryKey: ['inbox', 'send-operation', messageId],
    queryFn: () => api.messaging.getSendOperationForMessage(messageId),
    retry: false,
    refetchOnWindowFocus: false,
  });
  const mutation = useMutation({
    mutationFn: ({ id, request }: { id: string; request: MessageSendResolutionRequest }) =>
      api.messaging.resolveSend(id, request),
    retry: false,
  });
  const problem = useApiProblem(mutation.error ?? query.error);
  const data = query.data;
  const busy = mutation.isPending || query.isFetching;

  async function resolve(resolution: MessageSendResolutionRequest['resolution']) {
    if (
      !data ||
      busy ||
      query.isError ||
      inFlight.current ||
      (resolution === 'Confirmed' ? !data.canConfirm : !data.canMarkReviewed)
    )
      return;
    inFlight.current = true;
    setFeedback(null);
    try {
      const result = await mutation.mutateAsync({
        id: data.id,
        request: {
          expectedRevision: data.revision,
          expectedDispatchAttemptId: data.dispatchAttemptId,
          resolution,
          providerMessageId: data.providerMessageId,
          evidenceReference: `review_${crypto.randomUUID()}`,
          reason: resolution === 'Confirmed' ? 'provider_acceptance' : 'evidence_inconclusive',
        },
      });
      setFeedback(
        result.state === 'Confirmed'
          ? 'Die Annahme wurde vom Anbieter bestätigt.'
          : 'Prüfung vermerkt. Der Versand bleibt unklar.',
      );
      await cache.invalidateQueries({ queryKey: ['inbox'] });
    } catch {
      await query.refetch();
    } finally {
      inFlight.current = false;
    }
  }
  return {
    query,
    mutation,
    data,
    problem,
    busy,
    feedback,
    resolve,
    canClose: () => !inFlight.current,
  };
}
